/**
 * State + actions for Requirement Lifecycle Management
 *
 * Follows the requirement-doctor convention: a custom hook holding local React
 * state and async action callbacks, rather than a global store library. Errors
 * from the API layer are surfaced via `error`; callers also get the thrown
 * value back (callbacks return a boolean / value) so they can branch without
 * reading stale state.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from './api';
import { ApiError } from '../../utils/apiClient';
import {
  type ImportMode,
  type RequirementInstance,
  type RequirementThread,
  type ThreadStatus,
} from './types';

/** A pending Split awaiting review: the parent and its PENDING_SPLIT children. */
export interface SplitReview {
  parent: RequirementThread;
  children: RequirementThread[];
}

/** A pending Merge awaiting review: the PENDING_MERGE target and its locked (WIP) sources. */
export interface MergeReview {
  target: RequirementThread | null;
  sources: RequirementThread[];
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

export interface ThreadFilter {
  status?: ThreadStatus;
  search?: string;
  includeTerminal?: boolean;
  page?: number;
  pageSize?: number;
}

export const PAGE_SIZE_DEFAULT = 50;

/** A related-thread list cut short by the page cap. */
class RelatedThreadsIncompleteError extends Error {
  constructor(readonly loaded: number, readonly total: number) {
    super(
      `Loaded ${loaded} of ${total} related threads; the list is incomplete`,
    );
    this.name = 'RelatedThreadsIncompleteError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Lists every thread matching a lineage filter. A Split/Merge review missing a
 * child or source is reviewed wrongly, so a list cut short by the page cap is
 * an error rather than a partial result.
 */
async function listAllRelatives(
  params: Omit<api.ListThreadsParams, 'limit' | 'offset'>,
): Promise<RequirementThread[]> {
  const { threads, total, complete } = await api.listAllThreads(params);
  if (!complete) {
    throw new RelatedThreadsIncompleteError(threads.length, total);
  }
  return threads;
}

export const useLifecycleStore = (projectId: number) => {
  const { t } = useTranslation('requirement-lifecycle');
  const [threads, setThreads] = useState<RequirementThread[]>([]);
  const [total, setTotal] = useState(0); // reserved for future pagination
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detail-screen state.
  const [thread, setThread] = useState<RequirementThread | null>(null);
  const [instances, setInstances] = useState<RequirementInstance[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const clearError = useCallback(() => setError(null), []);

  // Generation counter: each loadThreads call increments it; stale responses
  // are dropped so a slow first call never overwrites a faster second call.
  const genRef = useRef(0);

  // -- List (S-01) ----------------------------------------------------------

  const loadThreads = useCallback(
    async (filter: ThreadFilter = {}) => {
      const gen = ++genRef.current;
      setLoading(true);
      setError(null);
      try {
        const pageSize = filter.pageSize ?? PAGE_SIZE_DEFAULT;
        const page = filter.page ?? 0;
        const result = await api.listThreads({
          projectId,
          status: filter.status,
          search: filter.search,
          includeTerminal: filter.includeTerminal,
          limit: pageSize,
          offset: page * pageSize,
        });
        if (gen !== genRef.current) return false;
        setThreads(result.threads);
        setTotal(result.total);
        return true;
      } catch (err) {
        if (gen !== genRef.current) return false;
        setError(describeError(err));
        return false;
      } finally {
        if (gen === genRef.current) setLoading(false);
      }
    },
    [projectId],
  );

  // -- Registration (S-02) ------------------------

  const register = useCallback(
    async (texts: string[], mode: ImportMode) => {
      setError(null);
      try {
        const results = await api.registerRequirements(projectId, texts, mode);
        return results;
      } catch (err) {
        setError(describeError(err));
        return null;
      }
    },
    [projectId],
  );

  // -- Detail (S-03) --------------------------------------------------------

  const detailGenRef = useRef(0);

  const loadDetail = useCallback(async (threadId: string) => {
    const gen = ++detailGenRef.current;
    setDetailLoading(true);
    setError(null);
    try {
      // One round-trip: include_instances returns the thread + its versions.
      const { thread: th, instances: ins } = await api.getThread(threadId, {
        includeInstances: true,
      });
      if (gen !== detailGenRef.current) return false;
      setThread(th);
      setInstances(ins);
      return true;
    } catch (err) {
      if (gen !== detailGenRef.current) return false;
      setError(describeError(err));
      return false;
    } finally {
      if (gen === detailGenRef.current) setDetailLoading(false);
    }
  }, []);

  // Fetch a thread's instances without touching detail-view state. Used by the
  // Split entry from the list, which needs the current text but is not the
  // detail screen. Returns [] on error (the Split source text is display-only).
  const fetchInstances = useCallback(async (threadId: string) => {
    try {
      return await api.listInstances(threadId);
    } catch (err) {
      setError(describeError(err));
      return [];
    }
  }, []);

  // Errors of the related-thread loaders reach the error banner, so the two
  // raised here are translated; their English detail goes to the console.
  const describeRelativesError = useCallback(
    (err: unknown): string => {
      if (err instanceof RelatedThreadsIncompleteError) {
        console.error(err);
        return t('common.relatedThreadsIncomplete', {
          loaded: err.loaded,
          total: err.total,
        });
      }
      if (err instanceof api.LineageFilterUnsupportedError) {
        console.error(err);
        return t('common.lineageFilterUnsupported');
      }
      return describeError(err);
    },
    [t],
  );

  // -- Pending Split/Merge review re-entry --------------------
  // Reconstruct a pending proposal from persisted state so it can be reviewed
  // in a later session — not just inline right after Propose. Children/sources
  // are fetched by their lineage pointer + pending status.

  const loadSplitReview = useCallback(
    async (parentThreadId: string): Promise<api.SplitResult | null> => {
      setError(null);
      try {
        const [{ thread: parent }, children] = await Promise.all([
          api.getThread(parentThreadId),
          listAllRelatives({
            projectId,
            status: 'PENDING_SPLIT',
            parentThreadId,
          }),
        ]);
        return { parent, children };
      } catch (err) {
        setError(describeRelativesError(err));
        return null;
      }
    },
    [projectId, describeRelativesError],
  );

  // Downward lineage for the detail view: all threads pointing
  // back at this one as a split child (parentThreadId) or merge source
  // (mergedIntoThreadId), regardless of their current status (soft-deleted
  // excluded). Distinct from loadSplitReview/loadMergeReview, which intentionally
  // match only the PENDING_* states for review re-entry — lineage must keep
  // showing children/sources after the operation is accepted (children become
  // WIP, sources become MERGED).
  const loadLineageRelatives = useCallback(
    async (
      threadId: string,
    ): Promise<{
      children: RequirementThread[];
      sources: RequirementThread[];
    }> => {
      try {
        const [childList, sourceList] = await Promise.all([
          listAllRelatives({
            projectId,
            includeTerminal: true,
            parentThreadId: threadId,
          }),
          listAllRelatives({
            projectId,
            includeTerminal: true,
            mergedIntoThreadId: threadId,
          }),
        ]);
        // includeTerminal also returns DELETED threads; lineage leaves them out.
        const active = (list: RequirementThread[]) =>
          list.filter(thr => thr.status !== 'DELETED');
        return { children: active(childList), sources: active(sourceList) };
      } catch (err) {
        setError(describeRelativesError(err));
        return { children: [], sources: [] };
      }
    },
    [projectId, describeRelativesError],
  );

  const loadMergeReview = useCallback(
    async (targetThreadId: string): Promise<api.MergeResult | null> => {
      setError(null);
      try {
        const [{ thread: target }, sources] = await Promise.all([
          api.getThread(targetThreadId),
          // The sources stay WIP while the proposal is open;
          // only a locked source still points at its target as WIP — accept
          // makes it MERGED and reject clears the link.
          listAllRelatives({
            projectId,
            status: 'WIP',
            mergedIntoThreadId: targetThreadId,
          }),
        ]);
        // A review with no source has nothing to accept or reject: the merge
        // was resolved elsewhere, or the data is inconsistent.
        if (sources.length === 0) {
          setError(t('common.mergeReviewNoSources'));
          return null;
        }
        return { target, sources };
      } catch (err) {
        setError(describeRelativesError(err));
        return null;
      }
    },
    [projectId, describeRelativesError, t],
  );

  // -- Lineage name resolution -------------------------------
  // Resolve lineage thread ids → threads (for names/links). Missing ancestors
  // are silently dropped by the api layer.
  const resolveThreads = useCallback(async (ids: string[]) => {
    try {
      return await api.getThreadsByIds(ids);
    } catch (err) {
      setError(describeError(err));
      return [];
    }
  }, []);

  // Re-pull thread + instances after a mutation so the detail view is fresh.
  const refreshDetail = useCallback(async (threadId: string) => {
    try {
      const { thread: th, instances: ins } = await api.getThread(threadId, {
        includeInstances: true,
      });
      setThread(th);
      setInstances(ins);
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  /** Wraps a mutation: runs it, refreshes detail on success, captures errors. */
  const runMutation = useCallback(
    async (threadId: string, fn: () => Promise<unknown>) => {
      setError(null);
      try {
        await fn();
        await refreshDetail(threadId);
        return true;
      } catch (err) {
        setError(describeError(err));
        return false;
      }
    },
    [refreshDetail],
  );

  const editInstance = useCallback(
    (threadId: string, text: string) =>
      runMutation(threadId, () => api.editInstance(threadId, text)),
    [runMutation],
  );

  // Edit a thread's CURRENT text without touching detail-view state. Used by the
  // Merge review panel to revise the (WIP) integration target before Accept
  // Returns the new instance on success (caller uses its text
  // directly to avoid a second round-trip), or null on error.
  const editText = useCallback(async (threadId: string, text: string) => {
    setError(null);
    try {
      return await api.editInstance(threadId, text);
    } catch (err) {
      setError(describeError(err));
      return null;
    }
  }, []);

  const acceptInstance = useCallback(
    (threadId: string, versionId: string) =>
      runMutation(threadId, () => api.acceptInstance(threadId, versionId)),
    [runMutation],
  );

  const rejectInstance = useCallback(
    (threadId: string, versionId: string) =>
      runMutation(threadId, () => api.rejectInstance(threadId, versionId)),
    [runMutation],
  );

  const approveThread = useCallback(
    (threadId: string) =>
      runMutation(threadId, () => api.approveThread(threadId)),
    [runMutation],
  );

  /**
   * Force-approve through the ASPICE gate.
   *
   * Returns the gate's verdict rather than a bare boolean: `confirmed: false`
   * is a successful HTTP call that deliberately did nothing, and the caller has
   * to show the warnings and offer to re-send with confirm=true. Collapsing it
   * into "ok / not ok" is what made the button silently do nothing.
   *
   * Returns null when the request itself failed (the error is on `error`).
   */
  const forceApproveThread = useCallback(
    async (
      threadId: string,
      confirm = false,
    ): Promise<api.ForceApproveResult | null> => {
      setError(null);
      try {
        const result = await api.forceApproveThread(threadId, confirm);
        // Refresh either way: on confirm the thread moved, and on a block the
        // detail view should still reflect whatever the server now holds.
        await refreshDetail(threadId);
        return result;
      } catch (err) {
        setError(describeError(err));
        return null;
      }
    },
    [refreshDetail],
  );

  const unapproveThread = useCallback(
    (threadId: string) =>
      runMutation(threadId, () => api.unapproveThread(threadId)),
    [runMutation],
  );

  const updateMetadata = useCallback(
    (threadId: string, patch: Parameters<typeof api.updateThreadMetadata>[1]) =>
      runMutation(threadId, () => api.updateThreadMetadata(threadId, patch)),
    [runMutation],
  );

  // -- Submit (DRAFT → WIP) -----------------------------------
  // Returns a boolean; the caller decides whether to reload the list or detail.
  // (Not routed through runMutation since it is invoked from list rows too,
  // where no detail is loaded.)

  const submitThread = useCallback(async (threadId: string) => {
    setError(null);
    try {
      await api.submitThread(threadId);
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  }, []);

  // -- Delete (S-05) -----------------------------------------
  // Returns a boolean so the modal can stay open on failure.

  const deleteThread = useCallback(async (threadId: string) => {
    setError(null);
    try {
      await api.deleteThread(threadId);
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  }, []);

  // -- Split / Merge (S-04) ----------------------------
  // These return the result (or null on error) so the S-04 screen can render
  // the resulting pending state inline.

  const proposeSplit = useCallback(
    async (threadId: string, children: api.SplitChildInput[]) => {
      setError(null);
      try {
        return await api.proposeSplit(threadId, children);
      } catch (err) {
        setError(describeError(err));
        return null;
      }
    },
    [],
  );

  const proposeMerge = useCallback(
    async (params: {
      sourceThreadIds: string[];
      name: string;
      text: string;
    }) => {
      setError(null);
      try {
        return await api.proposeMerge({ projectId, ...params });
      } catch (err) {
        setError(describeError(err));
        return null;
      }
    },
    [projectId],
  );

  // Boolean-returning finalizers (accept/reject) for both Split and Merge.
  const finalize = useCallback(async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  }, []);

  const acceptSplit = useCallback(
    (threadId: string) => finalize(() => api.acceptSplit(threadId)),
    [finalize],
  );
  const rejectSplit = useCallback(
    (threadId: string) => finalize(() => api.rejectSplit(threadId)),
    [finalize],
  );
  const acceptMerge = useCallback(
    (targetThreadId: string) => finalize(() => api.acceptMerge(targetThreadId)),
    [finalize],
  );
  const rejectMerge = useCallback(
    (targetThreadId: string) => finalize(() => api.rejectMerge(targetThreadId)),
    [finalize],
  );

  return {
    // list state
    threads,
    total,
    loading,
    error,
    clearError,
    loadThreads,
    register,
    // detail state
    thread,
    instances,
    detailLoading,
    loadDetail,
    fetchInstances,
    refreshDetail,
    loadSplitReview,
    loadMergeReview,
    loadLineageRelatives,
    resolveThreads,
    editInstance,
    editText,
    acceptInstance,
    rejectInstance,
    approveThread,
    forceApproveThread,
    unapproveThread,
    updateMetadata,
    submitThread,
    deleteThread,
    // split / merge
    proposeSplit,
    acceptSplit,
    rejectSplit,
    proposeMerge,
    acceptMerge,
    rejectMerge,
  };
};

export type LifecycleStore = ReturnType<typeof useLifecycleStore>;
